import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';

export type StudentLoanDashboardFilterOptions = {
  facilityNames: string[];
};

export type StudentLoanTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalCurrentBalance: number;
  totalOrigBalance: number;
  loanCount: number;
  waGrossCouponPct: number | null;
  waFicoUpdated: number | null;
  waFicoOrig: number | null;
  delinq30PlusPctBal: number | null;
  cumulativeChargeOffBalance: number;
  cosignerPctBal: number | null;
  waRemainingTermMonths: number | null;
  inSchoolDeferForbearPctBal: number | null;
  facilityCount: number;
};

export type StudentLoanFacilityBalanceRow = {
  facilityName: string;
  currentBalance: number;
  loanCount: number;
};

export type StudentLoanCategorySlice = {
  category: string;
  balance: number;
};

export type StudentLoanPortfolioTrendRow = {
  asOfMonth: string;
  totalBalance: number;
  delinq30PlusPctBal: number | null;
};

export type StudentLoanFacilityMetricByMonthRow = {
  asOfMonth: string;
  facilityName: string;
  amount: number;
};

export type StudentLoanCategoryMetricByMonthRow = {
  asOfMonth: string;
  category: string;
  balance: number;
  portfolioBalanceTotal: number | null;
};

export type StudentLoanCategorySliceKind =
  | 'fico_band'
  | 'loan_status'
  | 'dpd_bucket'
  | 'school_type'
  | 'state_top10'
  | 'vintage'
  | 'repay_type'
  | 'grad_undergrad';

export type StudentLoanDashboardFilters = {
  facilityName: string[];
  asOfDate: string;
};

const FILTER_OPTIONS_PATH = 'student-loan-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseInt64(v: unknown): number | null {
  const n = parseNum(v);
  return n == null ? null : Math.trunc(n);
}

function appendFacilityAndAsOf(params: URLSearchParams, filters: StudentLoanDashboardFilters): void {
  for (const name of filters.facilityName) {
    const trimmed = name.trim();
    if (trimmed) params.append('facilityName', trimmed);
  }
  if (filters.asOfDate) {
    params.set('asOfDate', filters.asOfDate);
  }
}

function parseKpi(body: Record<string, unknown>): StudentLoanTapeDashboardKpiLatest {
  return {
    asOfDateIso: String(body.asOfDateIso ?? body.AsOfDateIso ?? ''),
    totalCurrentBalance: parseNum(body.totalCurrentBalance ?? body.TotalCurrentBalance) ?? 0,
    totalOrigBalance: parseNum(body.totalOrigBalance ?? body.TotalOrigBalance) ?? 0,
    loanCount: parseInt64(body.loanCount ?? body.LoanCount) ?? 0,
    waGrossCouponPct: parseNum(body.waGrossCouponPct ?? body.WaGrossCouponPct),
    waFicoUpdated: parseNum(body.waFicoUpdated ?? body.WaFicoUpdated),
    waFicoOrig: parseNum(body.waFicoOrig ?? body.WaFicoOrig),
    delinq30PlusPctBal: parseNum(body.delinq30PlusPctBal ?? body.Delinq30PlusPctBal),
    cumulativeChargeOffBalance: parseNum(body.cumulativeChargeOffBalance ?? body.CumulativeChargeOffBalance) ?? 0,
    cosignerPctBal: parseNum(body.cosignerPctBal ?? body.CosignerPctBal),
    waRemainingTermMonths: parseNum(body.waRemainingTermMonths ?? body.WaRemainingTermMonths),
    inSchoolDeferForbearPctBal: parseNum(body.inSchoolDeferForbearPctBal ?? body.InSchoolDeferForbearPctBal),
    facilityCount: parseInt64(body.facilityCount ?? body.FacilityCount) ?? 0,
  };
}

function parseFacilityRows(body: Record<string, unknown>): StudentLoanFacilityBalanceRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const facilityName = String(row.facilityName ?? row.FacilityName ?? '').trim();
      if (!facilityName) return null;
      return {
        facilityName,
        currentBalance: parseNum(row.currentBalance ?? row.CurrentBalance) ?? 0,
        loanCount: parseInt64(row.loanCount ?? row.LoanCount) ?? 0,
      };
    })
    .filter((r): r is StudentLoanFacilityBalanceRow => r != null);
}

function parseSlices(body: Record<string, unknown>): StudentLoanCategorySlice[] {
  const raw = body.slices ?? body.Slices;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const category = String(row.category ?? row.Category ?? '').trim() || 'Unknown';
      return {
        category,
        balance: parseNum(row.balance ?? row.Balance) ?? 0,
      };
    })
    .filter((r): r is StudentLoanCategorySlice => r != null);
}

function parseTrendRows(body: Record<string, unknown>): StudentLoanPortfolioTrendRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const asOfMonth = String(row.asOfMonth ?? row.AsOfMonth ?? '').trim();
      if (!asOfMonth) return null;
      return {
        asOfMonth,
        totalBalance: parseNum(row.totalBalance ?? row.TotalBalance) ?? 0,
        delinq30PlusPctBal: parseNum(row.delinq30PlusPctBal ?? row.Delinq30PlusPctBal),
      };
    })
    .filter((r): r is StudentLoanPortfolioTrendRow => r != null);
}

function parseFacilityMetricByMonthRows(body: Record<string, unknown>): StudentLoanFacilityMetricByMonthRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const asOfMonth = String(row.asOfMonth ?? row.AsOfMonth ?? '').trim();
      const facilityName = String(row.facilityName ?? row.FacilityName ?? '').trim();
      if (!asOfMonth || !facilityName) return null;
      return {
        asOfMonth,
        facilityName,
        amount: parseNum(row.amount ?? row.Amount) ?? 0,
      };
    })
    .filter((r): r is StudentLoanFacilityMetricByMonthRow => r != null);
}

function parseCategoryMetricByMonthRows(body: Record<string, unknown>): StudentLoanCategoryMetricByMonthRow[] {
  const raw = body.rows ?? body.Rows;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const asOfMonth = String(row.asOfMonth ?? row.AsOfMonth ?? '').trim();
      const category = String(row.category ?? row.Category ?? '').trim() || 'Unknown';
      if (!asOfMonth) return null;
      return {
        asOfMonth,
        category,
        balance: parseNum(row.balance ?? row.Balance) ?? 0,
        portfolioBalanceTotal: parseNum(row.portfolioBalanceTotal ?? row.PortfolioBalanceTotal),
      };
    })
    .filter((r): r is StudentLoanCategoryMetricByMonthRow => r != null);
}

/** Host stub for `/api/digitalconnect-service/student-loan-dashboard/*`. */
export class StudentLoanDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchFilterOptions = async (): Promise<StudentLoanDashboardFilterOptions> => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      facilityNames: stringArrayFromBody(body, 'facilityNames', 'FacilityNames'),
    };
  };

  fetchAsOfDates = async (): Promise<string[]> => {
    const body = await this.getJsonBody('student-loan-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchTapeDashboardKpi = async (filters: StudentLoanDashboardFilters): Promise<StudentLoanTapeDashboardKpiLatest> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/tape-kpi?${params.toString()}`);
    return parseKpi(body);
  };

  fetchFacilityBalances = async (filters: StudentLoanDashboardFilters): Promise<StudentLoanFacilityBalanceRow[]> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/facility-balances?${params.toString()}`);
    return parseFacilityRows(body);
  };

  fetchLoanCountByFacility = async (filters: StudentLoanDashboardFilters): Promise<StudentLoanFacilityBalanceRow[]> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/loan-count-by-facility?${params.toString()}`);
    return parseFacilityRows(body);
  };

  fetchCategorySlices = async (
    kind: StudentLoanCategorySliceKind,
    filters: StudentLoanDashboardFilters,
  ): Promise<StudentLoanCategorySlice[]> => {
    const params = new URLSearchParams();
    params.set('kind', kind);
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/category-slices?${params.toString()}`);
    return parseSlices(body);
  };

  fetchPortfolioTrend = async (filters: StudentLoanDashboardFilters): Promise<StudentLoanPortfolioTrendRow[]> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/portfolio-trend?${params.toString()}`);
    return parseTrendRows(body);
  };

  fetchFacilityBalancesOverTime = async (
    filters: StudentLoanDashboardFilters,
  ): Promise<StudentLoanFacilityMetricByMonthRow[]> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/facility-balances-over-time?${params.toString()}`);
    return parseFacilityMetricByMonthRows(body);
  };

  fetchLoanCountByFacilityOverTime = async (
    filters: StudentLoanDashboardFilters,
  ): Promise<StudentLoanFacilityMetricByMonthRow[]> => {
    const params = new URLSearchParams();
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/loan-count-by-facility-over-time?${params.toString()}`);
    return parseFacilityMetricByMonthRows(body);
  };

  fetchCategorySlicesOverTime = async (
    kind: StudentLoanCategorySliceKind,
    filters: StudentLoanDashboardFilters,
  ): Promise<StudentLoanCategoryMetricByMonthRow[]> => {
    const params = new URLSearchParams();
    params.set('kind', kind);
    appendFacilityAndAsOf(params, filters);
    const body = await this.getJsonBody(`student-loan-dashboard/category-slices-over-time?${params.toString()}`);
    return parseCategoryMetricByMonthRows(body);
  };
}
